// ============================================================
// تحویل فیلتر به صفحه
//
// کاربر دو شکل حرف می‌زند:
//
//   «رضا محمدی رو در صفحه وظایف راننده فیلتر کن»  → برو و فیلتر کن
//   «مهناز کافر فیلتر کن»                          → همین صفحه‌ای که هستم
//
// حالت اول با navigate انجام می‌شود و صفحه‌ی مقصد تازه mount می‌شود؛
// پس فیلتر در sessionStorage گذاشته می‌شود و صفحه در افکت mount
// برمی‌داردش.
//
// حالت دوم ناوبری ندارد و mount ای هم رخ نمی‌دهد. برای همین علاوه بر
// sessionStorage یک رویداد هم منتشر می‌شود؛ هوکِ صفحه به آن گوش
// می‌دهد و همان‌جا فیلتر را اعمال می‌کند.
//
// «برداشتن» فیلتر عمدی است: یک‌بار‌مصرف است. اگر پاک نشود، هر بار که
// کاربر به آن صفحه برگردد جست‌وجوی قدیمی دوباره اعمال می‌شود.
// ============================================================

import { useEffect } from "react";

const KEY = "havir-assistant-page-filter";
export const FILTER_EVENT = "havir-assistant-filter";

/** شناسه‌ی صفحه‌ها — همان id در SITE_PAGES و DATASETS. */
export type FilterPageId =
    | "bakeries"
    | "orders"
    | "quota"
    | "violations"
    | "users"
    | "driver-tasks"
    | "inspector-tasks"
    | "ai-tasks"
    | "reports";

export interface AssistantFilter {
    pageId: FilterPageId;
    /** عبارتی که در کادر جست‌وجوی صفحه گذاشته می‌شود */
    search?: string;
    /** تب وضعیت در صفحه‌های وظایف */
    status?: "not_done" | "done";
    /** تعداد سطر در هر صفحه — «۵۰ سطر نشون بده» */
    pageSize?: number;
    /** بازه‌ی تاریخ؛ فقط صفحه‌ی گزارش‌ها کادرش را دارد (YYYY/MM/DD شمسی) */
    startDate?: string;
    endDate?: string;
    /** دو پارامتر مخصوص گزارش توزیع آرد */
    voucherToDischargeDays?: number;
    dischargeToInspectionDays?: number;
}

/** مسیر هر صفحه‌ی فیلترپذیر — برای تشخیص «همین صفحه». */
export const FILTER_PAGE_ROUTES: Array<{ id: FilterPageId; path: string; title: string }> = [
    { id: "orders", path: "/manageorders", title: "حواله های نانوا" },
    { id: "driver-tasks", path: "/drivertasks", title: "وظایف راننده" },
    { id: "inspector-tasks", path: "/inspectortasks", title: "وظایف بازرس شهرستان" },
    { id: "ai-tasks", path: "/aitasks", title: "وظایف ناظر هوش مصنوعی" },
    { id: "quota", path: "/bakerquota", title: "سهمیه خبازی ها" },
    { id: "violations", path: "/violationsreport", title: "گزارش تخلفات" },
    { id: "users", path: "/manageuser", title: "مدیریت کاربران" },
    { id: "bakeries", path: "/bakeryinformation", title: "اطلاعات خبازی ها" },
    { id: "reports", path: "/reports", title: "گزارش ها" },
];

/** «همین صفحه» کدام است؟ null یعنی صفحه‌ی فعلی فیلترپذیر نیست. */
export const pageIdFromPath = (pathname: string): FilterPageId | null => {
    const path = String(pathname || "").toLowerCase();
    for (let i = 0; i < FILTER_PAGE_ROUTES.length; i++) {
        if (path.indexOf(FILTER_PAGE_ROUTES[i].path) === 0) return FILTER_PAGE_ROUTES[i].id;
    }
    return null;
};

export const currentPageId = (): FilterPageId | null =>
    typeof window === "undefined" ? null : pageIdFromPath(window.location.pathname);

export const putPageFilter = (filter: AssistantFilter): void => {
    try {
        sessionStorage.setItem(KEY, JSON.stringify(filter));
    } catch {
        /* حالت خصوصی مرورگر — صفحه بدون فیلتر باز می‌شود */
    }
};

/** فیلتر را فقط اگر برای همین صفحه بود برمی‌دارد و پاک می‌کند. */
export const takePageFilter = (pageId: FilterPageId): AssistantFilter | null => {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as AssistantFilter;
        if (!parsed || parsed.pageId !== pageId) return null;

        sessionStorage.removeItem(KEY);
        return parsed;
    } catch {
        return null;
    }
};

/** برای وقتی که کاربر همین حالا روی همان صفحه است. */
export const announcePageFilter = (filter: AssistantFilter): void => {
    putPageFilter(filter);
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(FILTER_EVENT, { detail: filter.pageId }));
    }
};

/**
 * در صفحه‌های فهرست صدا زده می‌شود:
 *
 *   useAssistantFilter("driver-tasks", (f) => {
 *       if (f.status) setActiveTab(f.status);
 *       setSearchTerm(f.search || "");
 *       setCurrentPage(1);
 *   });
 *
 * هم در mount (ناوبری از دستیار) و هم با رویداد (فیلتر روی همان
 * صفحه) اعمال می‌شود.
 */
export const useAssistantFilter = (
    pageId: FilterPageId,
    apply: (filter: AssistantFilter) => void
): void => {
    useEffect(() => {
        const consume = () => {
            const filter = takePageFilter(pageId);
            if (filter) apply(filter);
        };

        consume();
        window.addEventListener(FILTER_EVENT, consume);
        return () => window.removeEventListener(FILTER_EVENT, consume);
        // apply هر رندر تابع تازه است؛ اگر در وابستگی‌ها بیاید، لیسنر
        // مدام باز و بسته می‌شود. فیلتر یک‌بار‌مصرف است و این امن است.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageId]);
};
